import { isAxiosError } from 'axios';

import httpClient from './httpClient';

// Streaming session status as returned by pipeline-manager's
// streaming-session controller. STOPPED is the terminal state; anything
// else means the session is in flight to some degree (CREATING through
// LIVE through STOPPING).
export type StreamStatus =
  | 'CREATING'
  | 'CREATED'
  | 'INITIALIZING'
  | 'READY'
  | 'LIVE'
  | 'STOPPING'
  | 'STOPPED';

// The exercise the patient is working on right now, as pipeline-manager sees it.
//
// pipeline-manager is the only component that knows this: the app reports plan_index on every
// streaming/movement/start, and PM records it for the duration of the run. Optional at every
// level — absent between exercises, absent before the first one, and absent entirely from a
// pipeline-manager that predates the field. Treat a missing plan_index as "unknown", never as a
// slot number.
export type CurrentMovement = {
  plan_index?: number;
  sub_session_id?: string;
  treatment_plan_id?: string;
  started_at?: number;
};

export type ActiveStreamingSession = {
  session_id: string;
  stream_status: StreamStatus;
  current_movement?: CurrentMovement;
};

// GET /streaming/active_session — returns the patient's currently
// in-flight session, or undefined when none exists (pipeline-manager
// returns 204 in that case). Used by the Active Plan Case selector to
// disable switching while a session is running.
export async function getActiveStreamingSession(
  patientId: string,
  timeoutMs?: number,
): Promise<ActiveStreamingSession | undefined> {
  try {
    const response = await httpClient.get<ActiveStreamingSession>(
      '/streaming/active_session',
      {
        headers: { 'x-user-id': patientId },
        // 204 has no body; axios treats that as a successful response.
        validateStatus: (s) => s === 200 || s === 204,
        // Opt-in per call. httpClient sets no default timeout, so a dead socket
        // hangs indefinitely — RUM recorded GetActiveSession NETWORK_ERROR in the
        // >10s bucket 8 times in one week. The shared poller passes one because a
        // hung probe there now stalls every consumer at once, where previously
        // each had its own loop. Same scoping as the summary poll in
        // assessmentService: bound the polled call, not every portal request.
        ...(timeoutMs === undefined ? {} : { timeout: timeoutMs }),
      },
    );
    if (response.status === 204) {
      return undefined;
    }
    return response.data;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return undefined;
    }
    throw new Error(`Failed to fetch active streaming session: ${e}`);
  }
}

// True when an active streaming session exists and is in a NON-terminal
// state. Anything other than STOPPED is treated as "session in flight" —
// PATCHing the active plan case during these states could yield a plan
// the patient app already fetched, so we block the switch.
export function isSessionInFlight(session: ActiveStreamingSession | undefined): boolean {
  if (!session) {
    return false;
  }
  return session.stream_status !== 'STOPPED';
}
